/** @format */

import InventoryModule from '@/store/model/InventoryModule'
import { getModule } from 'vuex-module-decorators'
import store from '@/store'
import PaginateOptions from '@/models/interface/PaginateOptions'
import Api from '@/models/Api'
import WebMessage from '@/models/WebMessage'
import { clone as _clone } from 'lodash'
import moment from 'moment'
import AdunitScheduler from './AdunitScheduler'
import ColorManager from './interface/ColorManager'
import Company from './Company'
import SelectOption from './interface/SelectOption'

export default class Inventory {
  public id: string = ''

  public admanager_id: string = ''

  public name: string = ''

  public code: string = ''

  public ctv: boolean = false

  public desktop: boolean = false

  public mobile: boolean = false

  public live: boolean = false

  public length: string | null = null

  public group: string = 'sportstream_live'

  public publisher: Company | string | null = null

  public publisher_id: string = ''

  public publisher_name: string = ''

  public parent_id: string | null = null

  public status: boolean = true

  public scheduler_mode: string = 'on_off'

  public created_at: string = ''

  public updated_at: string = ''

  public hierarchy_ids: string[] = []

  public special_targeting: boolean = true

  public disable_schedulers: boolean = false

  public uses_key_values: boolean = false

  public is_fallback: boolean = false

  public allow_geo_override: boolean = false

  public fallback_inventory_id: null | string = null

  public cost: number = 0

  public rate: number = 0

  public adserver: string = 'cim'

  public apply_special_rate_to_children: boolean = false

  public special_rates: Array<any> = []

  public tracking_events: any = []

  public history_notes_count: number = 0

  public inventory_map: any = []

  public remote_connection: string | null = null

  public is_programmatic: boolean = false

  public category_authority: string = 'iab'

  public category_authority_version: string = '1.0'

  public remote_data: any = {
    remote_trafficker_id: '',
    remote_agency_id: '',
    remote_advertiser_id: '',
    force_device: '',
    force_dma: '',
    force_state: '',
    remote_inventory_ids: [] as string[],
    remote_content_ids: [] as string[],
    remote_label_ids: [] as string[],
  }

  public get full_name() {
    return `[${this.adserver.toUpperCase()}] ${this.name}`
  }

  public get devices(): string[] {
    const ret: string[] = []

    if (this.ctv) ret.push('ctv')
    if (this.desktop) ret.push('desktop')
    if (this.mobile) ret.push('mobile')

    return ret
  }

  public scheduler_ids: string[] = []

  public get schedulers(): Array<AdunitScheduler> {
    return AdunitScheduler.filter(this.scheduler_ids)
  }

  public set schedulers(schedulers: Array<AdunitScheduler>) {
    this.scheduler_ids = schedulers.map(scheduler => scheduler.id)
    AdunitScheduler.toObjectList(schedulers)
  }

  public get hierarchy(): Array<Inventory> {
    if (!this.hierarchy_ids) return []
    return Inventory.filter(this.hierarchy_ids)
  }

  public get parent(): Inventory | null {
    if (!this.parent_id) return null
    const parent = Inventory.module.data.find(i => i.id === this.parent_id)

    if (parent instanceof Inventory) {
      return parent
    }
    return null
  }

  public set hierarchy(hierarchy: Array<Inventory>) {
    Inventory.toObjectList(hierarchy)
  }

  public get allSchedulers(): AdunitScheduler[] {
    if (this.parent_id && this.parent) {
      return [...this.schedulers, ...this.parent.allSchedulers.filter(s => s.apply_to_children)]
    }
    return [...this.schedulers]
  }

  public get active_schedulers(): AdunitScheduler[] {
    return this.disable_schedulers
      ? []
      : this.allSchedulers.filter(
        s => s.status === 'active' && !s.exclude_adunit_ids.includes(this.id),
      )
  }

  public get has_schedulers(): boolean {
    return this.allSchedulers.length > 0
  }

  public get formLive(): boolean {
    return this.live
  }

  public set formLive(live: boolean) {
    this.live = live
    this.group = live ? 'sportstream_live' : 'sportstream_tv'
  }

  public get events() {
    if (this.allSchedulers.length === 0) return []

    const events: Array<any> = []
    let id = 0

    // Add global scheduler events
    const global_scheduler = AdunitScheduler.module.data.find(s => s.is_global)
    if (
      !this.disable_schedulers
      && this.active_schedulers.length > 0
      && global_scheduler
      && global_scheduler.status === 'active'
    ) {
      global_scheduler.events.forEach((source_event: any) => {
        const event = _clone(source_event)
        id++
        let color: string = ''
        if (id <= ColorManager.colors.length) {
          color = ColorManager.colors[id - 1]
        } else {
          let idx = id
          let amount = 0
          do {
            idx -= ColorManager.colors.length
            amount++
          } while (idx > ColorManager.colors.length)
          color = ColorManager.LightenDarkenColor(ColorManager.colors[idx - 1], amount * 25)
        }
        event._color = color
        event.full_id = `${global_scheduler.id}:${event.id}`
        event.name = `[GLOBAL SHCEDULER] ${event.name}`
        events.push(event)
      })
    }

    this.active_schedulers.forEach(scheduler => {
      scheduler.events.forEach((source_event: any) => {
        const event = _clone(source_event)
        id++
        let color: string = ''
        if (id <= ColorManager.colors.length) {
          color = ColorManager.colors[id - 1]
        } else {
          let idx = id
          let amount = 0
          do {
            idx -= ColorManager.colors.length
            amount++
          } while (idx > ColorManager.colors.length)
          color = ColorManager.LightenDarkenColor(ColorManager.colors[idx - 1], amount * 25)
        }
        event._color = color
        event.full_id = `${scheduler.id}:${event.id}`
        event.name = `[${scheduler.name}] ${event.name}`
        events.push(event)
      })
    })

    return events
  }

  public get calendarEvents() {
    return AdunitScheduler.buildCalendarEvents(this.events)
  }

  public static toObject(data: any, cache: boolean = true): Inventory {
    const inventory = new Inventory()

    inventory.id = data.id
    inventory.admanager_id = data.admanager_id
    inventory.name = data.name
    inventory.ctv = data.ctv
    inventory.desktop = data.desktop
    inventory.mobile = data.mobile
    inventory.live = data.live
    inventory.length = data.length
    inventory.group = data.group
    inventory.publisher_id = data.publisher_id
    inventory.publisher_name = data.publisher
    inventory.parent_id = data.parent_id
    inventory.status = data.status
    inventory.created_at = data.created_at
    inventory.updated_at = data.updated_at
    inventory.hierarchy_ids = data.hierarchy_ids
    inventory.code = data.code
    inventory.rate = data.rate
    inventory.adserver = data.adserver
    inventory.special_targeting = data.special_targeting
    inventory.scheduler_mode = data.scheduler_mode
    inventory.is_fallback = data.is_fallback
    inventory.allow_geo_override = data.allow_geo_override
    inventory.fallback_inventory_id = data.fallback_inventory_id
    if (data.special_rates) inventory.special_rates = data.special_rates
    inventory.apply_special_rate_to_children = data.apply_special_rate_to_children
    inventory.uses_key_values = data.uses_key_values
    inventory.disable_schedulers = data.disable_schedulers
    inventory.tracking_events = data.tracking_events || []
    inventory.category_authority = data.category_authority
    inventory.category_authority_version = data.category_authority_version

    inventory.is_programmatic = data.is_programmatic

    if (data.inventory_map) {
      inventory.inventory_map = data.inventory_map
    }

    if (data.hasOwnProperty('history_notes_count')) {
      inventory.history_notes_count = data.history_notes_count
    }

    if (data.publisher_model && typeof data.publisher_model === 'object') {
      inventory.publisher = Company.toObject(data.publisher_model)
    }
    if (data.remote_connection) {
      inventory.remote_connection = data.remote_connection
    }

    if (data.remote_data) {
      inventory.remote_data = { ...inventory.remote_data, ...data.remote_data }
    }

    if (data.schedulers) inventory.schedulers = data.schedulers
    if (data.hierarchy) inventory.hierarchy = data.hierarchy

    //  Cache Object List
    if (cache) Inventory.module.update(inventory)

    return inventory
  }

  public addInventoryMap() {
    this.inventory_map.push({
      code: '',
      target: '',
    })
  }

  public removeInventoryMap(index: number) {
    this.inventory_map.splice(index, 1)
  }

  public static toObjectList(data: any, cache: boolean = true): Inventory[] {
    const inventories = new Array<Inventory>()
    data.forEach((value: any) => {
      const inventory = Inventory.toObject(value, false)
      inventories.push(inventory)
    })

    //  Cache Object List
    if (cache) Inventory.module.update(inventories)

    return inventories
  }

  // State Management
  public static get module(): InventoryModule {
    if (!store.hasModule('inventory')) {
      store.registerModule('inventory', InventoryModule)
    }

    const m = getModule(InventoryModule)

    return m
  }

  public static async paginate(options: PaginateOptions) {
    return Inventory.module.paginate(options)
  }

  public static async get(id: string): Promise<Inventory | null> {
    return Inventory.module.find(id)
  }

  public getHistory(options: PaginateOptions) {
    const api = new Api()
    return api.get(`inventory/${this.id}/history`, options)
  }

  public deactivate() {
    this.status = false
    return this.save((response: any) => {
      WebMessage.success(`AdUnit "${this.name}" deactivated!`)
      return response
    })
  }

  public activate() {
    this.status = true
    return this.save((response: any) => {
      WebMessage.success(`AdUnit "${this.name}" activated!`)

      return response
    })
  }

  public disableSchedulers() {
    this.disable_schedulers = true
    return this.save((response: any) => {
      WebMessage.success(`AdUnit "${this.name}" schedulers disabled!`)
      return response
    })
  }

  public enableSchedulers() {
    this.disable_schedulers = false
    return this.save((response: any) => {
      WebMessage.success(`AdUnit "${this.name}" schedulers enabled!`)

      return response
    })
  }

  public save(callback: any = null) {
    const api = new Api()

    const data = {
      id: this.id,
      name: this.name,
      code: this.code,
      ctv: this.ctv,
      adserver: this.adserver,
      mobile: this.mobile,
      desktop: this.desktop,
      live: this.live,
      length: this.length,
      group: this.group,
      publisher: this.publisher_name,
      publisher_id: this.publisher_id,
      parent_id: this.parent_id,
      status: this.status,
      disable_schedulers: this.disable_schedulers,
      special_targeting: this.special_targeting,
      rate: this.rate,
      apply_special_rate_to_children: this.apply_special_rate_to_children,
      is_fallback: this.is_fallback,
      allow_geo_override: this.allow_geo_override,
      fallback_inventory_id: this.is_fallback ? null : this.fallback_inventory_id,
      special_rates: this.special_rates,
      uses_key_values: this.uses_key_values,
      scheduler_mode: this.adserver != 'cim' ? 'on_off' : this.scheduler_mode,
      tracking_events: this.tracking_events,
      inventory_map: this.inventory_map,
      remote_connection: this.remote_connection ?? null,
      remote_data: this.remote_data,
      is_programmatic: this.is_programmatic,
      category_authority: this.category_authority,
      category_authority_version: this.category_authority_version,
    }

    if (this.id) {
      return api
        .post(`inventory/${this.id}`, data)
        .then(callback ?? this.onSave)
        .catch(this.onError)
    }
    return api
      .post('inventory', data)
      .then(callback ?? this.onSave)
      .catch(this.onError)
  }

  private onSave(response: any) {
    const inventory = Inventory.toObject(response.data.result.inventory)
    WebMessage.success(`Inventory "${inventory.name}" saved!`)

    return response
  }

  private static onUpdate(response: any) {
    WebMessage.success(
      'Batch update complete! Please review the changes to ensure that it was applied correctly to the ad units.',
    )

    return response
  }

  private onError(error: any) {
    return error
  }

  public static buildTree(inventories: Array<Inventory>, parent_id: string | null = null): any {
    return inventories
      .filter(inventory => {
        if (parent_id === null) {
          return (
            inventory.parent_id === null
            || inventory.parent_id === '9237e7f6-f42b-481f-88c9-f0f66ae13506'
          )
        }
        return inventory.parent_id === parent_id
      })
      .map(inventory => ({
        text: `<strong>[${inventory.adserver.toUpperCase()}]</strong> ${inventory.name}`,
        id: inventory.id,
        children: Inventory.buildTree(inventories, inventory.id),
      }))
  }

  public static getTree(
    inventory: string | null = null,
    parent_id: string | null = null,
    query: string | null = null,
  ): any {
    const api = new Api()

    return api.get('inventories/tree', {
      inventory,
      parent_id,
      query,
    })
  }

  public static filter(filter: string[]): Inventory[] {
    if (Array.isArray(filter)) {
      return Inventory.module.data.filter(
        inventory => inventory.id && filter.includes(inventory.id),
      )
    }
    return Inventory.module.data.filter(filter)
  }

  public static async batchUpdate(data: any) {
    const api = new Api()

    return await api.post('inventory/batch-edit', { inventories: data }).then(this.onUpdate)
  }

  public static search(query: any) {
    const api = new Api()
    return api.get('inventory/search/option', query).then(response => response)
  }

  public static async searchByName(query: any) {
    const api = new Api()
    return api.get('inventories/search_name', query).then(response => response.data.result)
  }

  public static async loadInventories({ id, selected_inventory_ids }: any) {
    const api = new Api()
    return api
      .post('inventories/on_demand', { id, selected_inventory_ids })
      .then(response => response.data.result)
  }

  public static async loadReportNameOptions(query: any = null) {
    const api = new Api()
    return api
      .get('inventory/report-name-options', query)
      .then(response => response.data.result.options.map((v: string) => new SelectOption(v, v)))
  }

  public static async notifications() {
    const api = new Api()
    return api.get('inventory/notifications').then(response => {
      let res = response.data.result
      if (!res) return false

      return res
    })
  }

  public static async notificationsDismiss(payload: any) {
    const api = new Api()
    return api
      .post('inventory/notifications-dismiss', payload)
      .then(response => response.data.result)
  }

  public static async getLineItems(id: string, range: any, paginate: any) {
    const api = new Api()

    if (range) {
      range.start = moment(range.start).format('YYYY-MM-DD')
      range.end = moment(range.end).format('YYYY-MM-DD')
    }
    return api
      .get(`inventory/${id}/line-items`, { ...paginate, ...range })
      .then(response => response.data.result)
  }

  public async searchOptions(query: any) {
    const api = new Api(false)
    return api
      .get('inventory/search/option', query)
      .then(response => SelectOption.toObjectList(response.data.result.options))
      .catch(() => [])
  }

  public static async searchOptions(query: any) {
    return new this().searchOptions(query)
  }
}
